<template>
  <!-- 对齐平台独立窗 StandaloneAppShell：深蓝一级 + 白二级 + 顶栏 + 内容区 -->
  <div class="merchant-layout">
    <aside class="merchant-primary" aria-label="一级菜单">
      <button class="sidebar-brand" type="button" @click="goHome">
        <span class="sidebar-brand__icon" aria-hidden="true">
          <Icon icon="ep:shop" :size="18" />
        </span>
        <span class="sidebar-brand__text">{{ sidebarTitle }}</span>
      </button>
      <el-scrollbar always class="merchant-primary__scroll">
        <template v-if="useWorkbenchShell">
          <button
            v-for="item in primaryNavItems"
            :key="item.key"
            class="primary-item"
            :class="{ active: activePrimary?.key === item.key }"
            type="button"
            :title="item.title"
            @click="selectPrimary(item)"
          >
            <Icon :icon="item.icon || 'ep:menu'" :size="18" />
            <span>{{ item.title }}</span>
          </button>
        </template>
        <template v-else>
          <button
            v-for="item in legacyMenus"
            :key="item.path"
            class="primary-item"
            :class="{ active: route.path === item.path }"
            type="button"
            :title="item.title"
            @click="router.push(item.path)"
          >
            <Icon :icon="item.icon || 'ep:menu'" :size="18" />
            <span>{{ item.title }}</span>
          </button>
        </template>
      </el-scrollbar>
    </aside>

    <div class="merchant-main-column">
      <header class="merchant-header">
        <div class="header-left">
          <span class="header-title">{{ pageTitle }}</span>
        </div>
        <div class="header-user">
          <el-avatar :size="28">
            <span>{{ avatarLetter }}</span>
          </el-avatar>
          <span class="header-user__name">{{ sidebarTitle }}</span>
          <el-button type="danger" plain size="small" @click="handleLogout">
            <Icon icon="ep:switch-button" class="mr-5px" />退出
          </el-button>
        </div>
      </header>

      <div class="merchant-main-row">
        <aside
          v-if="useWorkbenchShell && showSecondary"
          class="merchant-secondary"
          aria-label="下级菜单"
        >
          <div class="secondary-title">{{ activePrimary?.title }}</div>
          <el-scrollbar>
            <el-menu
              class="secondary-menu"
              :default-active="activeMenu"
              router
            >
              <el-menu-item
                v-for="item in secondaryItems"
                :key="item.path"
                :index="item.path"
              >
                <Icon :icon="item.icon || 'ep:menu'" class="menu-icon" />
                <span>{{ item.title }}</span>
              </el-menu-item>
            </el-menu>
          </el-scrollbar>
        </aside>

        <div class="merchant-content">
          <div class="merchant-content-inner">
            <router-view />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useAppStore } from '@/store/modules/app'
import { removeMerchantToken } from '@/utils/auth'
import {
  COMMON_MENU,
  filterMenuByPermissions,
  getModule,
  isMenuItemAllowed,
  isPathAllowedForModules,
  LIVE_SECTION,
  type ModuleId
} from '@/views/merchant/registry/modules'
import {
  clearMerchantSession,
  getMerchantSession,
  getActiveModuleIds,
  patchMerchantShopName,
  setMerchantSession
} from '@/views/merchant/registry/merchant-session'
import { clearBConsoleSession } from '@/views/merchant/registry/session'
import {
  clusterMenuByGroup,
  getConsole,
  isPortalGuardEnabled,
  type ConsoleMenuItem
} from '@/views/merchant/registry/consoles'
import { getBConsoleSession } from '@/views/merchant/registry/session'
import { storePath } from '@/views/merchant/registry/paths'
import { getShopInfo } from '@/api/merchant/shop'
import { getWorkbenchContext } from '@/api/merchant/workbench'

defineOptions({ name: 'MerchantLayout' })

/** 一级导航：叶子（直接跳转）或业务域（点开白二级栏） */
interface PrimaryNavItem {
  key: string
  title: string
  icon?: string
  path?: string
  children?: ConsoleMenuItem[]
}

const route = useRoute()
const router = useRouter()
const appStore = useAppStore()
/** 商家端固定亮色；离开时恢复后台原暗色偏好（不写缓存） */
let previousDarkMode: boolean | undefined

/** 触发侧栏标题/会话重读（localStorage 变更本身不触发 computed） */
const sessionTick = ref(0)
const merchantSession = computed(() => {
  sessionTick.value
  return getMerchantSession()
})
const useWorkbenchShell = computed(() => !!merchantSession.value)

/** 左上角：店铺名优先，其次主体名 */
const sidebarTitle = computed(
  () =>
    merchantSession.value?.shopName ||
    merchantSession.value?.merchantName ||
    '商家管理中心'
)

const commonMenus = computed(() => {
  const sess = merchantSession.value
  const permissions = new Set(sess?.permissions || [])
  const moduleIds = new Set((sess?.modules || []).map((m) => m.moduleId))
  return COMMON_MENU.filter((item) => {
    if (item.requireModule && !moduleIds.has(item.requireModule)) return false
    return isMenuItemAllowed(item, permissions)
  })
})

const moduleSections = computed(() => {
  const sess = merchantSession.value
  if (!sess) return []
  const perms = new Set(sess.permissions || [])
  const moduleIds = new Set(sess.modules.map((m) => m.moduleId))
  const sections = sess.modules
    .map((ent) => {
      const desc = getModule(ent.moduleId as ModuleId)
      if (!desc) return null
      const menu = filterMenuByPermissions(desc.menu, perms, ent.moduleId as ModuleId).filter((item) => {
        if (item.requireModule && !moduleIds.has(item.requireModule)) return false
        return matchBizMode(item, sess.mallBizMode)
      })
      // 子账号无任何该模块菜单权限时，整块侧栏分组也不展示
      if (menu.length === 0) return null
      return {
        moduleId: ent.moduleId,
        sectionTitle: desc.sectionTitle,
        sectionIcon: desc.sectionIcon,
        menu
      }
    })
    .filter(Boolean) as {
      moduleId: string
      sectionTitle: string
      sectionIcon: string
      menu: ConsoleMenuItem[]
    }[]

  // 开通商城时，直播带货单独成「直播管理」一级大类
  if (moduleIds.has('MALL')) {
    const liveMenu = filterMenuByPermissions(LIVE_SECTION.menu, perms, 'MALL').filter((item) =>
      matchBizMode(item, sess.mallBizMode)
    )
    if (liveMenu.length > 0) {
      const liveSection = {
        moduleId: LIVE_SECTION.moduleId,
        sectionTitle: LIVE_SECTION.sectionTitle,
        sectionIcon: LIVE_SECTION.sectionIcon,
        menu: liveMenu
      }
      const afterIdx = sections.findIndex((s) => s.moduleId === LIVE_SECTION.insertAfterModuleId)
      if (afterIdx >= 0) {
        sections.splice(afterIdx + 1, 0, liveSection)
      } else {
        sections.push(liveSection)
      }
    }
  }
  return sections
})

/** 业务域一级菜单图标 */
const GROUP_MENU_ICONS: Record<string, string> = {
  商品中心: 'ep:goods',
  商品管理: 'ep:goods',
  订单履约: 'ep:document',
  财务中心: 'ep:wallet',
  财务管理: 'ep:wallet',
  营销中心: 'ep:present',
  营销管理: 'ep:present',
  店铺中心: 'ep:shop',
  店铺管理: 'ep:shop',
  客服中心: 'ep:chat-dot-round',
  客服服务: 'ep:chat-dot-round',
  社区团购: 'ep:shopping-bag',
  直播管理: 'ep:video-camera'
}

/**
 * 侧栏业务块：有 group → 平台式一级（点开出白二级）；无 group → 一级叶子
 */
const sidebarMenuBlocks = computed(() => {
  const multiRealModule =
    moduleSections.value.filter((s) => s.moduleId !== LIVE_SECTION.moduleId).length > 1
  const blocks: Array<{
    key: string
    group?: string
    icon?: string
    items: ConsoleMenuItem[]
  }> = []
  for (const section of moduleSections.value) {
    const clustered = clusterMenuByGroup(section.menu)
    const hasDomainGroups = clustered.some((c) => !!c.group)
    if (hasDomainGroups) {
      clustered.forEach((cluster, idx) => {
        const group = cluster.group
        blocks.push({
          key: `${section.moduleId}-${group || 'root'}-${idx}`,
          group,
          icon:
            (group && GROUP_MENU_ICONS[group]) ||
            cluster.items[0]?.icon ||
            section.sectionIcon ||
            'ep:menu',
          items: cluster.items
        })
      })
      continue
    }
    blocks.push({
      key: section.moduleId,
      group: multiRealModule ? section.sectionTitle : undefined,
      icon: section.sectionIcon || section.menu[0]?.icon || 'ep:menu',
      items: section.menu
    })
  }
  return blocks
})

/** 深蓝一级栏数据（通用叶子 + 业务域） */
const primaryNavItems = computed((): PrimaryNavItem[] => {
  const items: PrimaryNavItem[] = []
  for (const m of commonMenus.value) {
    items.push({
      key: `common-${m.path}`,
      title: m.title,
      icon: m.icon,
      path: m.path
    })
  }
  for (const block of sidebarMenuBlocks.value) {
    if (block.group && block.items.length) {
      items.push({
        key: block.key,
        title: block.group,
        icon: block.icon,
        children: block.items
      })
    } else {
      for (const m of block.items) {
        items.push({
          key: `leaf-${m.path}`,
          title: m.title,
          icon: m.icon,
          path: m.path
        })
      }
    }
  }
  return items
})

const pathInChildren = (item: PrimaryNavItem, path: string) =>
  Boolean(item.children?.some((c) => path === c.path || path.startsWith(c.path + '/')))

/** 当前路由命中的一级（业务域优先） */
const activePrimary = computed(() => {
  const path = route.path
  const withChildren = primaryNavItems.value.find(
    (item) => item.children?.length && pathInChildren(item, path)
  )
  if (withChildren) return withChildren
  return (
    primaryNavItems.value.find((item) => item.path && (path === item.path || path.startsWith(item.path + '/'))) ||
    primaryNavItems.value[0]
  )
})

const secondaryItems = computed(() => activePrimary.value?.children || [])
const showSecondary = computed(() => (secondaryItems.value?.length || 0) > 0)

function selectPrimary(item: PrimaryNavItem) {
  if (item.children?.length) {
    const stillIn = pathInChildren(item, route.path)
    if (!stillIn) {
      router.push(item.children[0].path)
    }
    return
  }
  if (item.path) {
    router.push(item.path)
  }
}

const legacyMenus = computed((): ConsoleMenuItem[] => {
  const s = getBConsoleSession()
  const desc = getConsole(s?.consoleId)
  const mode = merchantSession.value?.mallBizMode
  const perms = new Set(merchantSession.value?.permissions || [])
  const base = desc?.menu || []
  // 旧壳无分组：商城台仍扁平展示直播入口
  const withLive =
    desc?.consoleId === 'MALL_SHOP' ? [...base, ...LIVE_SECTION.menu] : base
  return withLive
    .filter((item) => matchBizMode(item, mode))
    .filter((item) => isMenuItemAllowed(item, perms))
})

function matchBizMode(item: ConsoleMenuItem, mode?: string | null) {
  return !item.requireBizMode || item.requireBizMode === mode
}

const pageTitle = computed(() => {
  for (const sec of moduleSections.value) {
    const hit = sec.menu.find((m) => m.path === route.path)
    if (hit) return hit.title
  }
  const common = commonMenus.value.find((m) => m.path === route.path)
  return common?.title || sidebarTitle.value
})

/** 顶栏头像字母：取店铺/主体名首字 */
const avatarLetter = computed(() => {
  const name = sidebarTitle.value?.trim()
  return name ? name.slice(0, 1) : '商'
})

const activeMenu = computed(() => route.path)

function goHome() {
  router.push(storePath('/workbench'))
}

/** 菜单项或其子页（如 /store/product/form）均视为可访问 */
function isVisibleMenuPath(path: string, visiblePaths: string[]): boolean {
  return visiblePaths.some((menuPath) => path === menuPath || path.startsWith(menuPath + '/'))
}

function guardDeepLink() {
  if (!isPortalGuardEnabled()) return
  if (useWorkbenchShell.value) {
    const ids = getActiveModuleIds()
    if (!isPathAllowedForModules(route.path, ids)) {
      ElMessage.warning('当前账号未开通该模块或无权访问')
      router.replace(storePath('/workbench'))
      return
    }
    const allVisiblePaths = [
      ...commonMenus.value.map((item) => item.path),
      ...moduleSections.value.flatMap((section) => section.menu.map((item) => item.path))
    ]
    // 侧栏只登记列表入口；隐藏子路由（商品表单等）须按父菜单放行，否则会被踢回工作台
    if (!isVisibleMenuPath(route.path, allVisiblePaths)) {
      ElMessage.warning('当前账号无权访问该功能')
      router.replace(storePath('/workbench'))
    }
    return
  }
  const s = getBConsoleSession()
  if (!s) {
    router.replace(storePath('/login'))
  }
}

async function handleLogout() {
  try {
    await ElMessageBox.confirm('确定要退出商家工作台吗？', '退出确认', { type: 'warning' })
  } catch {
    return
  }
  removeMerchantToken()
  clearBConsoleSession()
  clearMerchantSession()
  localStorage.removeItem('merchantId')
  localStorage.removeItem('merchantShopId')
  localStorage.removeItem('merchantUserType')
  localStorage.removeItem('merchantInstitutionId')
  localStorage.removeItem('capacityOrgId')
  router.replace(storePath('/login'))
}

onMounted(() => {
  previousDarkMode = appStore.getIsDark
  appStore.setIsDark(false, false)
  if (isPortalGuardEnabled() && !merchantSession.value && !getBConsoleSession()) {
    router.replace(storePath('/login'))
    return
  }
  guardDeepLink()
  window.addEventListener('merchant-shop-name-updated', onShopNameUpdated)
  void refreshShopTitleFromApi()
  void refreshWorkbenchSession()
})

onUnmounted(() => {
  window.removeEventListener('merchant-shop-name-updated', onShopNameUpdated)
  if (previousDarkMode !== undefined) {
    appStore.setIsDark(previousDarkMode, false)
    previousDarkMode = undefined
  }
})

function onShopNameUpdated() {
  sessionTick.value++
}

/** 已开通商城时拉一次店铺名，纠正历史会话里缓存的登录账号名 */
async function refreshWorkbenchSession() {
  if (!getMerchantSession()) return
  try {
    const ctx = await getWorkbenchContext()
    const sess = getMerchantSession()
    if (!sess) return
    setMerchantSession({
      ...sess,
      merchantName: ctx.shopName || ctx.merchantName || sess.merchantName,
      shopName: ctx.shopName || sess.shopName,
      modules: ctx.modules || sess.modules,
      permissions: ctx.permissions || [],
      mallBizMode: ctx.mallBizMode || undefined
    })
    sessionTick.value++
    guardDeepLink()
  } catch {
    /* 保持现有会话 */
  }
}

async function refreshShopTitleFromApi() {
  const sess = getMerchantSession()
  if (!sess?.modules?.some((m) => m.moduleId === 'MALL')) return
  try {
    const shop = await getShopInfo(true)
    const name = shop?.shopName?.trim()
    if (name) {
      patchMerchantShopName(name)
      sessionTick.value++
    }
  } catch {
    /* 非商城店或接口失败时保持主体名 */
  }
}

watch(
  () => route.path,
  () => guardDeepLink()
)
</script>

<style lang="scss" scoped>
/* Token / 布局对齐 StandaloneAppShell（sidebar-brand-layout） */
.merchant-layout {
  --merchant-topbar-height: 56px;
  --merchant-sidebar-width: 200px;
  --merchant-secondary-width: 166px;
  --merchant-sidebar-bg: #1e3a5f;
  --merchant-sidebar-text: #bccde0;
  --merchant-content-bg: #f0f2f5;

  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  color: #303133;
  background: var(--merchant-content-bg);
}

.merchant-primary {
  display: flex;
  width: var(--merchant-sidebar-width);
  overflow: hidden;
  color: var(--merchant-sidebar-text);
  background: var(--merchant-sidebar-bg);
  border-right: 0;
  flex: 0 0 var(--merchant-sidebar-width);
  flex-direction: column;
}

.sidebar-brand {
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: var(--merchant-topbar-height);
  min-height: var(--merchant-topbar-height);
  max-height: var(--merchant-topbar-height);
  padding: 0 16px;
  margin: 0;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgb(255 255 255 / 8%);
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  flex: 0 0 var(--merchant-topbar-height);
}

.sidebar-brand__icon {
  display: inline-flex;
  width: 28px;
  height: 28px;
  color: #fff;
  background: rgb(255 255 255 / 12%);
  border-radius: 6px;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.sidebar-brand__text {
  min-width: 0;
  overflow: hidden;
  font-size: 15px;
  font-weight: 700;
  line-height: 22px;
  color: #fff;
  letter-spacing: 0.02em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.merchant-primary__scroll {
  flex: 1 1 auto;
  min-height: 0;
}

.merchant-primary__scroll :deep(.el-scrollbar__view) {
  padding: 12px 0 48px;
}

.merchant-primary :deep(.el-scrollbar),
.merchant-primary :deep(.el-scrollbar__wrap),
.merchant-secondary :deep(.el-scrollbar),
.merchant-secondary :deep(.el-scrollbar__wrap) {
  height: 100%;
}

.merchant-primary :deep(.el-scrollbar__wrap),
.merchant-secondary :deep(.el-scrollbar__wrap) {
  overflow-x: hidden;
}

.primary-item {
  position: relative;
  display: flex;
  width: 100%;
  min-height: 52px;
  padding: 16px 22px;
  margin: 0;
  font: inherit;
  font-size: 14px;
  line-height: 22px;
  color: inherit;
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 0;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.primary-item :deep(.iconify) {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  overflow: visible;
}

.primary-item span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.primary-item:hover {
  color: #fff;
  background: rgb(255 255 255 / 6%);
}

.primary-item.active {
  font-weight: 600;
  color: #fff;
  background: rgb(36 75 130 / 50%);
  box-shadow: none;
}

.primary-item.active::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  content: '';
  background: #3b9eff;
}

.merchant-main-column {
  display: flex;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--merchant-content-bg);
  flex: 1 1 auto;
  flex-direction: column;
}

.merchant-header {
  display: flex;
  box-sizing: border-box;
  height: var(--merchant-topbar-height);
  min-height: var(--merchant-topbar-height);
  max-height: var(--merchant-topbar-height);
  padding: 0 24px;
  background: #fff;
  border-bottom: 1px solid #e5e9f0;
  box-shadow: 0 2px 8px rgb(31 45 61 / 5%);
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex: 0 0 var(--merchant-topbar-height);
}

.header-title {
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-user {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}

.header-user__name {
  max-width: 160px;
  overflow: hidden;
  font-size: 13px;
  color: #4b5563;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.merchant-main-row {
  display: flex;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  flex: 1 1 0%;
}

.merchant-secondary {
  display: flex;
  width: var(--merchant-secondary-width);
  min-width: 0;
  overflow: hidden;
  background: #fff;
  border-right: 1px solid #e6eaf0;
  box-shadow: 2px 0 8px rgb(31 45 61 / 4%);
  flex: 0 0 var(--merchant-secondary-width);
  flex-direction: column;
}

.secondary-title {
  display: flex;
  min-height: 52px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #1f2937;
  overflow-wrap: anywhere;
  border-bottom: 1px solid #edf0f4;
  align-items: center;
}

.secondary-menu {
  padding: 6px 8px 12px;
  border-right: 0;
}

.merchant-secondary :deep(.el-menu-item) {
  height: auto;
  min-height: 42px;
  padding-top: 9px;
  padding-right: 10px;
  padding-bottom: 9px;
  font-size: 13px;
  line-height: 20px;
  white-space: normal;
  overflow-wrap: anywhere;
  border-radius: 4px;
}

.merchant-secondary :deep(.el-menu-item:hover) {
  background: #f3f7fb;
}

.merchant-secondary :deep(.el-menu-item.is-active) {
  font-weight: 500;
  color: #1890ff;
  background: #e6f7ff;
}

.menu-icon {
  margin-right: 8px;
  font-size: 15px;
  vertical-align: middle;
}

.merchant-content {
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--merchant-content-bg);
  flex: 1 1 0%;
  flex-direction: column;
}

.merchant-content-inner {
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 0;
  padding: 20px 24px;
  overflow: auto;
}

.merchant-content-inner :deep(.el-card) {
  border-radius: 8px;
}

@media (width <= 900px) {
  .merchant-layout {
    --merchant-sidebar-width: 184px;
    --merchant-secondary-width: 148px;
  }

  .header-user__name {
    display: none;
  }

  .merchant-content-inner {
    padding: 16px;
  }
}
</style>
